/**
 * The Gnexis policy body.
 *
 * SEPARATE FROM THE DEFAULT ON PURPOSE, and the reason is not styling. A
 * privacy policy is a factual statement about ONE deployment, and the facts
 * that differ between deployments are exactly the ones a reader cares about:
 *
 *   · which AI provider receives their prompts (Gnexis: OpenAI only, because
 *     every entry in MODEL_CATALOG is `provider: 'openai'` and the host has no
 *     ANTHROPIC_API_KEY. The default text names three.)
 *   · where the servers are (Gnexis: Singapore. The default names none.)
 *   · which sub-processors exist at all (Gnexis adds Brave Search, AWS KMS and
 *     GitHub, and drops two model providers.)
 *
 * Written for a Malaysian operator with customers anywhere, so it states its
 * obligations in plain terms rather than citing one country's statute: a
 * jurisdiction-neutral promise is true everywhere, where a citation is true in
 * one place and merely confusing elsewhere. If a specific regime ever needs
 * naming, add a SECTION here. Do not fork this file into two documents. Two
 * policies for one product drift, and the drift is a legal statement.
 *
 * Every factual claim below was read out of the code rather than assumed:
 *   · model provider            shared/types/src/models.ts (MODEL_CATALOG)
 *   · web search                features/agent/tools/web-search.ts (Brave)
 *   · the share-link email      serve.ts writes shareLinkVerifications: a code
 *                               row with an expiry, used to check the 6-digit
 *                               code and resolve the session identity. It is
 *                               NOT surfaced to the share owner: `recentForShare`
 *                               has test callers only and no UI reads
 *                               verifications. Do not say the owner is shown who
 *                               opened a link until something actually shows it.
 *   · share-link access logging NONE. #1312 (owner 2026-10-23) emptied
 *                               `share_link_accesses` (migration 0335): its only
 *                               writer (removed) stored a Save-to-Drive saver's LOGIN email
 *                               and a bare hash of their user id, public links
 *                               included. Do not describe who opened or saved a
 *                               link, a visitor hash, or a salted / rotated one
 *                               (`tenant_ip_salt` had NO callers; emptied, 0338) until one exists.
 *   · public-link visitors      never recorded (#776, #1312): a public link's
 *                               edits are attributed to the link, and a shortcut
 *                               a signed-in visitor plants there carries no email.
 *   · link-record retention     share_link_verifications carries onDelete:'cascade'
 *                               from shareLinks: deleted with the link. NOTHING
 *                               wrote share_link_daily_stats (emptied, 0338).
 *   · credential custody        shared/auth/src/master-key-provider.ts. NOTE the
 *                               shape: `DecryptCommand` RETURNS the plaintext
 *                               key and the process holds it in memory, so
 *                               "the key never leaves KMS" and "we cannot
 *                               extract it" are BOTH false. The true and still
 *                               reassuring claim is that it is never written to
 *                               disk, so storage and backups do not contain it.
 *   · account deletion          shared/auth/src/account.ts (FK walk over the
 *                               user AND their personal tenant)
 *   · "essential cookies only"  no analytics or tracking SDK exists in apps/web
 * If any of those change, this file is wrong and must change with them.
 *
 * NOTE ON HOUSE STYLE: no em or en dashes. A test asserts it against the
 * rendered DOM, so a dash pasted in from a word processor fails the suite.
 *
 * NOT LEGAL ADVICE AND NOT REVIEWED BY COUNSEL.
 */
import type { PolicyContext, Section } from './types'

/**
 * Last substantive review of THIS body. Hand-maintained on purpose: a build
 * date would tick on every unrelated deploy and tell a reader the policy was
 * reviewed when nothing was read. Per body, so editing one brand's text does
 * not restamp another brand's.
 */
export const LAST_UPDATED = '23 October 2026'

export function buildGnexisSections(
  { productName, company, contactEmail }: PolicyContext,
): Section[] {
  return [
    {
      id: 'who-we-are',
      heading: 'Who we are',
      body: (
        <>
          <p>
            {productName} is operated by {company}. In this policy, &ldquo;we&rdquo;,
            &ldquo;us&rdquo; and &ldquo;our&rdquo; mean {company}. Our company number and
            registered address are at the end of this page.
          </p>
          <p>
            This policy explains what personal data we handle when you use {productName},
            why we handle it, who else sees it, and what you can ask us to do about it. It
            applies to the {productName} application, the apps published on it, and the
            links you share from it.
          </p>
        </>
      ),
    },
    {
      id: 'commitments',
      heading: 'What we will never do',
      body: (
        <>
          <p>
            These are commitments, not a description of what we happen to do at the moment.
            If any of them ever has to change, we will tell you before it changes, not after.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We will never sell your personal data, or hand it to anyone for their own purposes.</li>
            <li>We will never use your files, documents or conversations to train an AI model, ours or anyone else&rsquo;s.</li>
            <li>We will never show you advertising, or build a profile of you for advertisers.</li>
            <li>We will never track you across other websites. There is no analytics tracker and no third-party script on {productName}.</li>
            <li>We will never read your content out of curiosity or to build something of our own.</li>
          </ul>
          <p>
            That last one deserves the detail rather than the slogan. Nobody at {company} reads
            your files or your conversations as a matter of course. Named members of our staff
            can open a workspace to install or repair something, that access is logged, and it
            is used for nothing else.
          </p>
        </>
      ),
    },
    {
      id: 'two-roles',
      heading: 'Two different roles, and why it matters which one applies',
      body: (
        <>
          <p>
            {productName} is used by organisations, which means we handle personal data in
            two quite different capacities. Your rights differ between them.
          </p>
          <p>
            <strong className="themed-text">Your account.</strong> For the data that makes
            your account work, meaning your name, email address and settings, we decide how
            it is used. Requests about that data come to us.
          </p>
          <p>
            <strong className="themed-text">Your organisation&rsquo;s content.</strong> For
            files, documents, apps and conversations inside a workspace, the organisation
            that owns the workspace decides what is stored and who may see it. We hold and
            process that content on their instructions.
          </p>
          <p>
            If you want content removed or corrected, ask your organisation&rsquo;s
            administrator first. We will direct you to them, because acting on it ourselves
            would override their control of their own records.
          </p>
        </>
      ),
    },
    {
      id: 'your-content',
      heading: 'What you put into Gnexis, and what happens to it',
      body: (
        <>
          <p>
            This is the part people expect a privacy policy to be evasive about, so we will be
            plain. The things you create in {productName} are yours. We hold them so the
            product works, and for no other reason.
          </p>
          <p>
            <strong className="themed-text">Your files.</strong> What you upload or create,
            their contents, their earlier versions, how they are arranged in folders, and who
            you have shared them with. We keep earlier versions so you can go back to one, and
            we keep the sharing record so the right people keep access.
          </p>
          <p>
            <strong className="themed-text">Your conversations with the assistant.</strong>{' '}
            What you asked, what it answered, and the files it read or wrote while doing what
            you asked. We keep them so the conversation is still there when you come back, and
            so you can see what the assistant did on your behalf.
          </p>
          <p>
            <strong className="themed-text">Files you attach in a chat.</strong> When you
            attach a file to a message, we may record which account attached it, how big it
            was and when. That is what lets each member see how much chat storage they are
            using, and lets an organisation&rsquo;s administrators set and manage an
            allowance per member.
          </p>
          <p>
            An administrator sees the <strong className="themed-text">figures</strong>: how
            much someone has used, against what allowance. It does not give them a way into
            the contents of your chat files.
          </p>
          <p>
            Once a conversation has gone 12 months without activity, we remove it along with
            its files and this record.
          </p>
          <p>
            <strong className="themed-text">What your apps store.</strong> An app keeps its own
            working records, in its own database or its own folder. What goes in there is
            decided by your organisation, not by us, and it may include personal data about
            other people.
          </p>
          <p>
            None of it is scanned for advertising, mined to build products, or shared outside
            your organisation except where you have chosen to share it. Section&nbsp;5 explains
            the one circumstance in which your content leaves our servers, which is when you
            ask the assistant a question.
          </p>
        </>
      ),
    },
    {
      id: 'records',
      heading: `What ${productName} records in order to work`,
      body: (
        <>
          <p>
            Separate from your content, and much smaller: the records the service itself needs
            to run. Each one is here because something would not work without it. {productName}
            is actively developed, so this section describes what we may record rather than a
            frozen list, and the commitments in section 2 apply to all of it.
          </p>
          <p>
            <strong className="themed-text">Your account.</strong> Your name, email address,
            whether that address has been verified, your profile picture if you set one, and
            your preferences, including whether you have asked us not to send you promotional
            email. An account needs an owner, a sign-in code needs somewhere to go, and we have
            to remember a request to stop in order to honour it. If you sign in with Google, your name, email address and picture come to us
            from Google, and your Google password never does.
          </p>
          <p>
            <strong className="themed-text">How much you used.</strong> Which model ran, how
            many tokens it used, how long a task took, and which workspace it belonged to. This
            is what limits and invoices are calculated from.{' '}
            <strong className="themed-text">
              These records describe volume, not content.
            </strong>{' '}
            They say that a request was made and how large it was, never what was in it.
          </p>
          <p>
            <strong className="themed-text">What happens to what you share.</strong> We do not
            keep a log of who opened a link you shared or who saved a file from it.{' '}
            <strong className="themed-text">We do not store IP addresses.</strong> On a public
            link we never record who a visitor is: what they change is recorded as made through
            the link.
          </p>
          <p>
            <strong className="themed-text">Proving who you are on a shared link.</strong> If a
            link asks a visitor for an email address, we send a code to it and use it to confirm
            they are allowed to open the link and to identify them while they are using it. The
            address is recorded with the share.
          </p>
          <p>
            <strong className="themed-text">Payments.</strong> If your organisation pays by
            card, Stripe handles the payment. Card numbers go to Stripe and never reach our
            servers. We keep the amount and whether it was paid.
          </p>
          <p>
            We do not ask for, and ask that you do not put into {productName}, data about health,
            religious belief, political opinion or anything else a person would consider
            sensitive in that way.
          </p>
        </>
      ),
    },
    {
      id: 'why',
      heading: 'Why we handle it',
      body: (
        <>
          <p>To provide {productName}: signing you in, storing your files, running apps and the assistant, and letting you share.</p>
          <p>To keep it secure: detecting abuse, investigating incidents, and enforcing usage limits.</p>
          <p>To bill for it: measuring usage and issuing invoices.</p>
          <p>To support you: answering questions and fixing faults you report.</p>
          <p>
            To tell you about {productName}: occasional messages about new features, changes
            and offers. Every one of them carries a way to stop them, and stopping them does not
            affect the messages you need in order to use the service, such as sign-in codes,
            invitations and alerts.
          </p>
          <p>To meet our legal obligations, including keeping financial records.</p>
        </>
      ),
    },
    {
      id: 'ai',
      heading: 'What the AI assistant sends elsewhere',
      body: (
        <>
          <p>
            This section is deliberately specific, because it is the question people most
            often want answered.
          </p>
          <p>
            When you use the assistant, your message and whatever file content is needed to
            answer it are sent to <strong className="themed-text">OpenAI</strong>, who run the
            models {productName} offers. The model picker names the model before you choose
            it.
          </p>
          <p>
            If you ask the assistant to search the web, your search query, and not your files,
            is sent to <strong className="themed-text">Brave Search</strong>.
          </p>
          <p>
            Your content is sent so the model can respond to you. We do not sell it, and{' '}
            <strong className="themed-text">
              we do not use your content to train any model, ours or a provider&rsquo;s.
            </strong>{' '}
            Each provider&rsquo;s handling of it is governed by their contract with us.
          </p>
          <p>
            OpenAI is the model provider {productName} uses today. We may use others as the
            product develops, and we keep the list below current. Whoever runs the model, the
            commitments in section 2 do not change: your content is never sold, and never used
            to train a model.
          </p>
          <p>
            If you do not want a document&rsquo;s contents leaving our servers, do not ask the
            assistant to work on it. Your organisation&rsquo;s administrator can also restrict
            which models are available.
          </p>
        </>
      ),
    },
    {
      id: 'apps',
      heading: `Apps on ${productName}`,
      body: (
        <>
          <p>
            {productName} opens onto a set of apps. An app runs inside your
            organisation&rsquo;s workspace and can read and write the files in its own folder.
            Where an app may call another app, or be run by someone your organisation has
            shared it with, that is a permission your organisation&rsquo;s administrator
            grants explicitly.
          </p>
          <p>
            An app can also run without anyone watching, on a schedule or when a file changes.
            When it does, it acts with the permissions your organisation gave it, not with
            yours.
          </p>
          <p>
            <strong className="themed-text">Who is responsible for an app depends on who made
            it.</strong> Apps we publish are ours, and this policy covers them. An app your own
            organisation builds is your organisation&rsquo;s: it decides what the app collects
            and what it does with it, and we simply hold the result. If you use an app another
            organisation has shared with you, that organisation is responsible for what it
            collects, and you will be told what the app can do before you use it.
          </p>
        </>
      ),
    },
    {
      id: 'sharing',
      heading: 'Sharing outside your organisation',
      body: (
        <>
          <p>
            When you share a file or an app by link, the person who opens it sees what you
            shared and nothing else. Depending on how the link was created, they may be asked
            for an email address before it opens, and they may be asked to approve what the
            app is allowed to do on their behalf.
          </p>
          <p>
            What we record about that visit is described under &ldquo;What {productName}{' '}
            records in order to work&rdquo; above. A visitor&rsquo;s email address is used to
            confirm and administer their access to what you shared, and for nothing else.
          </p>
        </>
      ),
    },
    {
      id: 'connected-accounts',
      heading: 'Connected accounts and stored credentials',
      body: (
        <>
          <p>
            If you connect an outside service to {productName}, we store the credential that
            connection issues so the app or the assistant can act on your behalf. You can
            disconnect it at any time, which deletes the stored credential.
          </p>
          <p>
            Those credentials are encrypted, and the key that decrypts them is never written to
            our disks in plaintext. See &ldquo;How we protect it&rdquo; below.
          </p>
        </>
      ),
    },
    {
      id: 'who-else',
      heading: 'Who else sees your data',
      body: (
        <>
          <p>
            We do not sell personal data. We share it only with the service providers below,
            each doing one specific job for us:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="themed-text">OpenAI</strong>: runs the AI models. Sees your messages and the file content needed to answer them.</li>
            <li><strong className="themed-text">Brave Search</strong>: web search for the assistant. Sees search queries only.</li>
            <li><strong className="themed-text">Stripe</strong>: card payments. Card details go to Stripe and never reach us.</li>
            <li><strong className="themed-text">Resend</strong>: delivers email such as sign-in codes, notifications and invitations.</li>
            <li><strong className="themed-text">Cloudflare</strong>: serves and protects the site. Sees network traffic in transit, including IP addresses.</li>
            <li><strong className="themed-text">Hetzner</strong>: hosts the servers your data is stored on. Stores the data at rest and does not access it.</li>
            <li><strong className="themed-text">Amazon Web Services</strong>: holds the master key protecting stored credentials. Sees key material only, never your files or messages.</li>
            <li><strong className="themed-text">GitHub</strong>: only if you connect a GitHub account, and only what that connection is scoped to.</li>
          </ul>
          <p>
            <strong className="themed-text">Our own access.</strong> Named members of our staff
            can access a workspace to install, maintain or repair the apps and the platform.
            That access is logged. It is not used to read your content for any other purpose.
          </p>
          <p>
            We may add, remove or change providers as {productName} develops, and we keep this
            list current. A new provider does not loosen anything in section 2.
          </p>
          <p>
            We may also disclose data where the law requires it, or to establish or defend a
            legal claim.
          </p>
        </>
      ),
    },
    {
      id: 'where',
      heading: 'Where your data is kept',
      body: (
        <>
          <p>
            Your files, workspaces and account records are stored on servers in{' '}
            <strong className="themed-text">Singapore</strong>. If we ever add a location, we
            will say so here. The master key protecting
            stored credentials is held by Amazon Web Services in Germany. Cloudflare serves
            the site from locations worldwide. OpenAI, Brave, Stripe and Resend process the
            data described above outside Malaysia.
          </p>
          <p>
            Your personal data therefore crosses borders in the ordinary course of providing
            {' '}{productName}. We transfer it only to providers who are contractually bound to
            protect it to a standard at least equivalent to our own.
          </p>
        </>
      ),
    },
    {
      id: 'cookies',
      heading: 'Cookies',
      body: (
        <>
          <p>
            {productName} sets only the cookies it needs to work, principally the one that
            keeps you signed in.
          </p>
          <p>
            There is no advertising network, no analytics tracker and no third-party profiling
            script on this site. We do not track you across other websites.
          </p>
        </>
      ),
    },
    {
      id: 'security',
      heading: 'How we protect it',
      body: (
        <>
          <p>Traffic between you and {productName} is encrypted in transit.</p>
          <p>
            Each organisation&rsquo;s data is isolated at the database level, so one
            organisation&rsquo;s queries cannot reach another&rsquo;s records.
          </p>
          <p>Passwords are stored hashed, never in a readable form.</p>
          <p>
            Stored third-party credentials are encrypted. The key that decrypts them is itself
            kept only in encrypted form, and is unwrapped by a managed key service when our
            servers start. It is held in memory while they run and is never written to disk, so
            someone who obtained a copy of our storage or our backups would not find it.
          </p>
          <p>
            Where a log needs to tell one person&rsquo;s activity from another&rsquo;s, it
            holds a one-way hash rather than anything that identifies them directly.
          </p>
          <p>
            Code the assistant runs on your behalf runs in an isolated sandbox with no access
            to any other organisation&rsquo;s data.
          </p>
          <p>No system is perfectly secure, and we do not claim otherwise.</p>
        </>
      ),
    },
    {
      id: 'breach',
      heading: 'If something goes wrong',
      body: (
        <p>
          If a breach affects your personal data, we will investigate it, take steps to
          contain it, and tell you without unnecessary delay where it is likely to cause you
          harm. Where we hold data on your organisation&rsquo;s behalf, we will tell that
          organisation so they can inform the people affected. We will notify the relevant
          authorities where we are required to.
        </p>
      ),
    },
    {
      id: 'retention',
      heading: 'How long we keep it',
      body: (
        <>
          <p>
            <strong className="themed-text">Your account and content:</strong> for as long as
            the account or workspace exists.
          </p>
          <p>
            <strong className="themed-text">Deleted files:</strong> they go to the bin and can
            be restored until your organisation empties it.
          </p>
          <p>
            <strong className="themed-text">Records about a link you shared:</strong> kept for
            as long as the share link itself exists, and no longer than we need them. Delete the
            link and they are deleted with it.
          </p>
          <p>
            <strong className="themed-text">Billing and accounting records:</strong> kept as
            long as the law requires, even after an account closes. Where we can, we strip out
            the personal details and keep only the amounts.
          </p>
          <p>
            <strong className="themed-text">When you delete your account:</strong> your
            account and your personal workspace, including its files, are deleted, as are the
            access you were given in organisations, your team memberships and any requests of
            yours still pending.
          </p>
          <p>
            What you did inside an organisation&rsquo;s workspace stays with that organisation,
            because it is their record rather than yours to remove: content you contributed,
            access you granted, keys and collections you created, settings you changed, and the
            organisation&rsquo;s activity and audit entries, which can include your IP address and
            device details. These records keep showing your email address, marked as a deleted
            account. The billing and accounting entries above stay too.
          </p>
        </>
      ),
    },
    {
      id: 'rights',
      heading: 'Your choices and rights',
      body: (
        <>
          <p>You may:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="themed-text">Ask for a copy</strong> of the personal data we hold about you.</li>
            <li><strong className="themed-text">Ask us to correct it</strong> if it is wrong or out of date.</li>
            <li><strong className="themed-text">Ask us to delete it</strong>, where we are not required to keep it.</li>
            <li><strong className="themed-text">Ask us to send it to you</strong> in a commonly used format, so you can take it elsewhere.</li>
            <li><strong className="themed-text">Withdraw your consent</strong> to our handling it, though this may mean we can no longer provide {productName} to you.</li>
            <li><strong className="themed-text">Tell us to stop</strong> sending you anything promotional, at any time and without giving a reason. Every promotional message carries a link that does this, or you can write to us.</li>
            <li><strong className="themed-text">Ask us to limit</strong> how we handle it while a question about it is resolved.</li>
          </ul>
          <p>
            {contactEmail
              ? <>Write to <a href={`mailto:${contactEmail}`} className="themed-text hover:underline">{contactEmail}</a> and we will respond within 21 days. We may need to verify who you are first.</>
              : <>Use the contact details below. We will respond within 21 days, and may need to verify who you are first.</>}
          </p>
          <p>
            For content held inside an organisation&rsquo;s workspace, see &ldquo;Two different
            roles&rdquo; above. Those requests go to the organisation.
          </p>
          <p>
            If you are not satisfied with our response, you may complain to the data protection
            authority for your country.
          </p>
        </>
      ),
    },
    {
      id: 'children',
      heading: 'Children',
      body: (
        <p>
          {productName} is provided to organisations for work. It is not intended for anyone
          under 18, and we do not knowingly collect their personal data. If you believe a child
          has given us personal data, contact us and we will delete it.
        </p>
      ),
    },
  ]
}
